import { useEffect, useRef, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { PRESENCE_OPTIONS, getCsConnectionId } from '../csConstants';

function isHeartbeatEligible(desiredStatus) {
  return desiredStatus === 'ONLINE' || desiredStatus === 'AWAY' || desiredStatus === 'BUSY';
}

export default function PresenceBar({ onAgentChange }) {
  const [agent, setAgent] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const agentRef = useRef(null);
  agentRef.current = agent;

  async function loadMe() {
    try {
      const me = await adminApi.imCsAgentMe();
      setAgent(me);
      onAgentChange?.(me);
      setError('');
      return me;
    } catch (err) {
      setError(err.message || '載入 Presence 失敗');
      return null;
    }
  }

  useEffect(() => {
    const connectionId = getCsConnectionId();
    loadMe();

    const timer = setInterval(async () => {
      const desired = agentRef.current?.desiredStatus;
      if (!isHeartbeatEligible(desired)) {
        return;
      }
      try {
        const resp = await adminApi.imCsHeartbeat({
          connectionId,
          workspaceHint: 'WORKBENCH',
        });
        // Soft no-op while OFFLINE (e.g. Redis TTL expired) — stop until user re-selects status
        if (resp?.desiredStatus === 'OFFLINE' || resp?.effectiveStatus === 'OFFLINE') {
          const me = await loadMe();
          if (me && !isHeartbeatEligible(me.desiredStatus)) {
            return;
          }
        }
      } catch {
        /* ignore transient heartbeat errors */
      }
    }, 20000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function changePresence(desiredStatus) {
    setSaving(true);
    setError('');
    try {
      const connectionId = getCsConnectionId();
      const me = await adminApi.imCsUpdatePresence({
        desiredStatus,
        // Establish / renew lease when leaving OFFLINE
        connectionId: desiredStatus === 'OFFLINE' ? undefined : connectionId,
      });
      setAgent(me);
      onAgentChange?.(me);
    } catch (err) {
      setError(err.message || '更新狀態失敗');
    } finally {
      setSaving(false);
    }
  }

  const status = agent?.presenceStatus || agent?.desiredStatus || 'OFFLINE';

  return (
    <div className="cs-presence">
      <div className="cs-presence__status">
        <span className={`cs-presence__dot cs-presence__dot--${status}`} />
        {agent?.displayName || agent?.username || '客服'}
      </div>
      <label>
        狀態{' '}
        <select
          value={agent?.desiredStatus || 'OFFLINE'}
          disabled={saving || !agent}
          onChange={(e) => changePresence(e.target.value)}
        >
          {PRESENCE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </label>
      <span className="cs-presence__meta">
        容量 {agent?.currentActiveSessions ?? 0}/{agent?.maxConcurrentSessions ?? '—'}
        {agent?.roleType ? ` · ${agent.roleType}` : ''}
      </span>
      {error ? <span className="cs-error">{error}</span> : null}
    </div>
  );
}
